import type { A2UIMessage } from '@atlassian/a2ui-renderer/types/A2UIProtocol.types';
import { ACTIONABLE_FOLLOW_UPS_MARKER } from '@atlassian/conversation-assistant-content-transformer/utils/actionable-follow-ups';
import type { StreamResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { ACTIONABLE_FOLLOW_UPS_COMPONENT_KEY } from './ACTIONABLE_FOLLOW_UPS_COMPONENT_KEY';
import { extractA2UIFromStructuredContent } from './extractA2UIFromStructuredContent';
import { extractMarkdownContent } from './extractMarkdownContent';
import { preprocessStreamResponseDetails } from './preprocessStreamResponseDetails';

export type FinalizedMessageNormalizationOptions = {
	shouldExtractA2UI: boolean;
	shouldExtractActionableFollowUps: boolean;
	shouldExtractDataSculptor: boolean;
	shouldExtractInlineLumina: boolean;
	shouldExtractLegacyChartBoundaries: boolean;
	shouldPreprocessDetails: boolean;
};

export type FinalizedMessageExtractionCounts = {
	actionableFollowUps: number;
	dataSculptor: number;
	expand: number;
	inlineLumina: number;
	legacyChart: number;
	unicodeChart: number;
};

export type FinalizedMessagePresentation = {
	agenticUiMessages?: A2UIMessage[];
	extractionCounts: FinalizedMessageExtractionCounts;
	markdownData: Record<string, string>;
	message: StreamResponse;
};

const cloneStreamResponse = (message: StreamResponse): StreamResponse => ({
	...message,
	message: {
		...message.message,
		message: {
			...message.message.message,
		},
	},
});

export const normalizeFinalizedMessagePresentation = ({
	message,
	options,
}: {
	message: StreamResponse;
	options: FinalizedMessageNormalizationOptions;
}): FinalizedMessagePresentation => {
	let processedMessage = cloneStreamResponse(message);
	const markdownData: Record<string, string> = {};
	const extractionCounts: FinalizedMessageExtractionCounts = {
		actionableFollowUps: 0,
		dataSculptor: 0,
		expand: 0,
		inlineLumina: 0,
		legacyChart: 0,
		unicodeChart: 0,
	};

	if (options.shouldPreprocessDetails) {
		processedMessage = preprocessStreamResponseDetails(processedMessage);
	}

	if (options.shouldExtractDataSculptor) {
		const sculptorMsg = extractMarkdownContent({
			streamMessage: processedMessage,
			boundaryStart: '[^sculptor:start^]',
			boundaryEnd: '[^sculptor:end^]',
			replacementContent: '[^sculptor^]',
		});
		processedMessage = sculptorMsg.processedStreamMessage as StreamResponse;
		sculptorMsg.boundaryContents.forEach((content, index) => {
			markdownData[`sculptor-${index}`] = content;
		});
		extractionCounts.dataSculptor = sculptorMsg.boundaryContents.length;
	}

	const expandOpenTag = '▞';
	const expandCloseTag = '▚';
	const expandPlaceholder = '[^expand^]';

	const expandMsg = extractMarkdownContent({
		streamMessage: processedMessage,
		boundaryStart: expandOpenTag,
		boundaryEnd: expandCloseTag,
		replacementContent: expandPlaceholder,
	});
	processedMessage = expandMsg.processedStreamMessage as StreamResponse;
	expandMsg.boundaryContents.forEach((content, index) => {
		markdownData[`expand-${index}`] = content;
	});
	extractionCounts.expand = expandMsg.boundaryContents.length;

	let chartIndex = 0;
	if (options.shouldExtractLegacyChartBoundaries) {
		const chartMsg = extractMarkdownContent({
			streamMessage: processedMessage,
			boundaryStart: '[^chart:start^]',
			boundaryEnd: '[^chart:end^]',
			replacementContent: '[^chart^]',
		});
		processedMessage = chartMsg.processedStreamMessage as StreamResponse;
		chartMsg.boundaryContents.forEach((content) => {
			markdownData[`chart-${chartIndex}`] = content;
			chartIndex++;
		});
		extractionCounts.legacyChart = chartMsg.boundaryContents.length;
	}

	const unicodeChartMsg = extractMarkdownContent({
		streamMessage: processedMessage,
		boundaryStart: '◴',
		boundaryEnd: '◶',
		replacementContent: '[^chart^]',
	});
	processedMessage = unicodeChartMsg.processedStreamMessage as StreamResponse;
	unicodeChartMsg.boundaryContents.forEach((content) => {
		markdownData[`chart-${chartIndex}`] = content;
		chartIndex++;
	});
	extractionCounts.unicodeChart = unicodeChartMsg.boundaryContents.length;

	if (options.shouldExtractInlineLumina) {
		const luminaMsg = extractMarkdownContent({
			streamMessage: processedMessage,
			boundaryStart: '◐',
			boundaryEnd: '◑',
			replacementContent: '\n\n[^lumina^]\n\n',
		});
		processedMessage = luminaMsg.processedStreamMessage as StreamResponse;
		luminaMsg.boundaryContents.forEach((content, index) => {
			markdownData[`lumina-${index}`] = content;
		});
		extractionCounts.inlineLumina = luminaMsg.boundaryContents.length;
	}

	if (options.shouldExtractActionableFollowUps) {
		const followUpsMsg = extractMarkdownContent({
			streamMessage: processedMessage,
			boundaryStart: ACTIONABLE_FOLLOW_UPS_MARKER,
			boundaryEnd: '',
			replacementContent: `\n\n[^${ACTIONABLE_FOLLOW_UPS_COMPONENT_KEY}^]\n\n`,
		});
		processedMessage = followUpsMsg.processedStreamMessage as StreamResponse;
		followUpsMsg.boundaryContents.forEach((content, index) => {
			markdownData[`${ACTIONABLE_FOLLOW_UPS_COMPONENT_KEY}-${index}`] = content;
		});
		extractionCounts.actionableFollowUps = followUpsMsg.boundaryContents.length;
	}

	let agenticUiMessages: A2UIMessage[] | undefined;
	if (options.shouldExtractA2UI) {
		const a2uiResult = extractA2UIFromStructuredContent(processedMessage.message.message.content);
		if (a2uiResult) {
			processedMessage.message.message.content = a2uiResult.textContent;
			agenticUiMessages = a2uiResult.a2uiMessages;
		}
	}

	return {
		agenticUiMessages,
		extractionCounts,
		markdownData,
		message: processedMessage,
	};
};
